import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLogs } from "../../app/hooks";
import Loading from "../common/Loading";
import Error from "../common/Error";
import { Clear } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { LogSearchKind } from "../../app/backendTypes";
import UserPicker from "../common/users/UserPicker";

export default function Logs() {
  const [kind, setKind] = useState<LogSearchKind>(LogSearchKind.User);
  const [valueField, setValueField] = useState("");
  const [value, setValue] = useState("");

  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const k = params.get("kind");
    if (k !== null) {
      setKind(k as LogSearchKind);
    }
  }, [params]);

  useEffect(() => {
    const v = params.get("value");
    if (v !== null) {
      setValueField(v);
      setValue(v);
    }
  }, [params]);

  const payload = useMemo(
    () => (kind && value ? { kind, value } : null),
    [kind, value]
  );

  const { logs, error, loading, reload } = useLogs(payload);

  const handleLogKindChange = (event: SelectChangeEvent) => {
    const kind = event.target.value as LogSearchKind;

    setKind(kind);
    setValueField("");
    setValue("");
    setParams({ kind });
  };

  let comp = <></>;
  if (loading) comp = <Loading />;
  else if (error) comp = <Error error={error} />;
  else if (logs.length === 0) comp = <Typography>No logs found</Typography>;
  else comp = <LogsView logs={logs} />;

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
        <FormControl fullWidth size="small" sx={{ maxWidth: "sm" }}>
          <InputLabel id="log-kind">Log Kind</InputLabel>
          <Select
            labelId="log-kind"
            value={kind}
            label="Log Kind"
            onChange={handleLogKindChange}
          >
            {Object.keys(LogSearchKind).map((k, i) => (
              <MenuItem key={i} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {kind === LogSearchKind.User ? (
          <>
            <UserPicker
              initialValue={parseInt(valueField)}
              onChange={(user) =>
                setValueField(user?.employee.id.toString() || "")
              }
              small
              disableCreate
              disableClearable
            />
          </>
        ) : (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>{kind === "Custom" ? "Regex" : "UUID"}</InputLabel>
              <OutlinedInput
                label={kind === "Custom" ? "Regex" : "UUID"}
                value={valueField}
                onChange={(e) => setValueField(e.target.value)}
                endAdornment={
                  <IconButton
                    onClick={() => {
                      setValueField("");
                      setParams({ kind });
                    }}
                    sx={{
                      visibility: valueField.length > 0 ? "visible" : "hidden",
                    }}
                  >
                    <Clear />
                  </IconButton>
                }
              />
            </FormControl>
          </>
        )}

        <Button
          variant="outlined"
          onClick={() => {
            if (valueField.length === 0) setParams({ kind });
            if (value === valueField) reload();
            else {
              setValue(valueField);
              setParams({ kind, value: valueField });
            }
          }}
        >
          Run
        </Button>
      </Box>

      {comp}
    </>
  );
}

function LogsView(props: { logs: string[] }) {
  const { logs } = props;

  return (
    <Typography
      variant="caption"
      width="100%"
      fontFamily="monospace"
      color="text.secondary"
    >
      {logs.map((l, i) => (
        <Fragment key={i}>
          {l}
          <br />
        </Fragment>
      ))}
    </Typography>
  );
}
